import { BookOpenText } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * SeeAlsoFooter — bottom-of-article cross-link card list.
 *
 * Design plan reference,
 * section 22.3 (article footer) + section 22.4 (reference page footer).
 *
 * Frontmatter shape (per plan §4) is `see_also: [{topic_id}|...]`.
 * Resolving topic_id -> {title, href} is the Astro build's job; this
 * component takes already-resolved entries and renders them as a
 * stacked card list with a small "See also" header and book icon.
 */

export interface SeeAlsoEntry {
  title: string
  href: string
  /** Optional one-line description rendered below the title. */
  description?: string
}

export interface SeeAlsoFooterProps extends React.HTMLAttributes<HTMLElement> {
  entries: SeeAlsoEntry[]
  /** Override the header label. Defaults to 'See also'. */
  label?: string
}

export const SeeAlsoFooter = React.forwardRef<HTMLElement, SeeAlsoFooterProps>(
  ({ entries, label = 'See also', className, ...rest }, ref) => {
    if (entries.length === 0) return null

    return (
      <aside
        ref={ref}
        aria-label={label}
        className={cn(
          'rounded-lg border border-ia-border bg-card p-4',
          className,
        )}
        {...rest}
      >
        <div className="flex items-center gap-2 mb-3">
          <BookOpenText size={14} className="text-ia-green-text" aria-hidden="true" />
          <span className="text-body-sm font-semibold text-ia-text">{label}</span>
        </div>
        <ul className="flex flex-col gap-2 m-0 p-0 list-none">
          {entries.map((entry) => (
            <li key={entry.href} className="m-0">
              <a
                href={entry.href}
                className={cn(
                  'group flex flex-col gap-0.5 rounded-md px-2 py-1.5 -mx-2',
                  'transition-colors duration-100 motion-reduce:transition-none',
                  'hover:bg-ia-surface-hover',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                <span className="text-body-sm font-medium text-ia-text group-hover:text-ia-green-text">
                  {entry.title}
                </span>
                {entry.description && (
                  <span className="text-caption text-ia-text-secondary">
                    {entry.description}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </aside>
    )
  },
)
SeeAlsoFooter.displayName = 'SeeAlsoFooter'
